package music.jammers.client.shared.event.track.presentation.viewmodel

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import music.jammers.client.shared.analytics.event.EventTrackAnalytics
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.update
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.track.domain.usecase.AddEventTrackUseCase
import music.jammers.client.shared.event.track.domain.usecase.ValidateEventTrackUseCase
import music.jammers.client.shared.event.track.domain.validator.EventTrackFieldValidator
import music.jammers.client.shared.event.track.presentation.model.UpdateInstrument
import music.jammers.client.shared.event.track.presentation.model.toEventInstrumentList
import music.jammers.shared.event.domain.model.EventInstrument
import music.jammers.shared.event.track.domain.model.AddEventTrack
import music.limasoul.client.shared.event.domain.usecase.GetEventDetailsUseCase
import music.limasoul.client.shared.navigation.Router

class AddEventTrackViewModel(
    private val addEventTrackUseCase: AddEventTrackUseCase,
    private val eventTrackAnalytics: EventTrackAnalytics,
    private val eventTrackFieldValidator: EventTrackFieldValidator,
    private val validateEventTrackUseCase: ValidateEventTrackUseCase,
    private val getEventDetailsUseCase: GetEventDetailsUseCase,
    private val router: Router,
    initState: AddEventTrackState = AddEventTrackState(),
) : BaseViewModel() {

    internal val state: State<AddEventTrackState> get() = _state
    internal val validationState: State<FieldValidationState> get() = _validationState

    private val _state = mutableStateOf(initState)
    private val _validationState = mutableStateOf(FieldValidationState())

    private val errorHandler = customExceptionHandler(router) { _, throwable ->
        eventTrackAnalytics.onError(throwable)

        _state.update {
            copy(
                progress = false,
                error = throwable,
            )
        }
    }

    internal fun onReady(eventId: Long) {
        _state.update {
            copy(
                eventId = eventId,
                progress = true
            )
        }

        launch(errorHandler) {
            val eventDetails = getEventDetailsUseCase(eventId)

            _state.update {
                copy(
                    progress = false,
                    selectedInstruments = eventDetails.eventInstrumentList.map { item ->
                        val status = when (item.status) {
                            EventInstrument.Status.OPTIONAL -> UpdateInstrument.Status.OPTIONAL
                            else -> UpdateInstrument.Status.REQUIRED
                        }

                        UpdateInstrument(
                            eventInstrument = item,
                            status = mutableStateOf(status)
                        )
                    },
                )
            }
        }
    }

    internal fun onBackPressed() {
        router.back()
    }

    internal fun onArtistChanged(value: String) {
        _state.update {
            copy(
                artistName = value
            )
        }

        validateField(ValidateEventTrackUseCase.ARTIST_NAME, value)
    }

    internal fun onTrackChanged(value: String) {
        _state.update {
            copy(
                trackTitle = value
            )
        }

        validateField(ValidateEventTrackUseCase.TRACK_NAME, value)
    }

    internal fun onCommentChanged(value: String) {
        _state.update {
            copy(
                comment = value
            )
        }
    }

    internal fun onAddTrackClicked() {
        val stateValue = state.value

        val addEventTrack = AddEventTrack(
            trackName = stateValue.trackTitle,
            artistName = stateValue.artistName,
            comment = stateValue.comment,
            instruments = stateValue.selectedInstruments.toEventInstrumentList(),
        )

        val errors = validateEventTrackUseCase.invoke(
            artistName = addEventTrack.artistName,
            trackName = addEventTrack.trackName,
            instruments = stateValue.selectedInstruments,
        )

        if (errors.isEmpty()) {
            createTrack(addEventTrack)
        } else {
            _validationState.update {
                copy(
                    validationErrorMap = errors.associateBy { item ->
                        item.fieldName
                    }
                )
            }
            _state.update {
                copy(
                    addButtonEnabled = errors.isEmpty()
                )
            }
        }
    }

    private fun createTrack(addEventTrack: AddEventTrack) {
        _state.update {
            copy(
                progress = true
            )
        }

        launch(errorHandler) {
            val eventId = state.value.eventId
            val eventTrack = addEventTrackUseCase(eventId, addEventTrack)

            eventTrackAnalytics.onTrackAdded(eventTrack.id)

            router.back()
        }
    }

    internal fun onErrorShown() {
        _state.update {
            copy(
                error = null
            )
        }
    }

    private fun validateField(name: String, value: String) {
        _validationState.update {
            val error = eventTrackFieldValidator.validate(name, value)
            if (error == null) {
                resetError(name)
            } else {
                addError(error)
            }
        }
        _state.update {
            copy(
                addButtonEnabled = validationState.value.errors.isEmpty()
            )
        }
    }

    private fun validateInstruments(selected: List<UpdateInstrument>) {
        _validationState.update {
            val error = eventTrackFieldValidator.validate(selected)
            if (error == null) {
                resetError(ValidateEventUseCase.SELECTED_INSTRUMENTS)
            } else {
                addError(error)
            }
        }
        _state.update {
            copy(
                addButtonEnabled = validationState.value.errors.isEmpty()
            )
        }
    }

    internal fun onInstrumentClicked(item: UpdateInstrument) {
        val nextStatus = when (item.status.value) {
            UpdateInstrument.Status.REQUIRED -> UpdateInstrument.Status.OPTIONAL
            UpdateInstrument.Status.OPTIONAL -> UpdateInstrument.Status.NOT_REQUIRED
            UpdateInstrument.Status.NOT_REQUIRED -> UpdateInstrument.Status.REQUIRED
        }

        item.status.value = nextStatus

        validateInstruments(_state.value.selectedInstruments)
    }
}